import { push } from 'connected-react-router';
import {
  removeCartItem,
  removeCartItems,
  resetRetouching,
  V3_ADD_INCENTIVE_TO_ORDER,
  V3_ADD_TO_CART,
  V3_DECLINE_INTERSTITIAL_PRODUCT,
  V3_DECLINE_PERSONALIZATION,
  V3_DECLINE_PRE_CART_OFFER_PRODUCT,
  V3_DECLINE_RETOUCHING,
  V3_REMOVE_CART_ITEM,
  V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS,
  V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS,
  V3_SUBMIT_PERSONALIZATION,
} from '../actions/v3Order';
import {
  ADD_VOUCHER_TO_CART,
  REMOVE_CART_VOUCHER_ITEM,
  clearCartVoucherItems,
} from '../actions/action_cart_voucher_items';
import cartUtils from '../utils/cart';
import { productIsAllGroup } from '../components/v3/products/imageRecords';
// Selectors are plain functions of state, so the middleware shares the same
// incentive-catalog derivation the components read via useSelector.
import {
  selectRestrictionAwareIncentiveProducts,
  selectCartLevelIncentiveProducts,
  selectIgnoredCartLevelIncentiveSkus,
  selectIgnoredOrderIncentiveIds,
  selectShippingRestricted,
} from '../selectors';

const postConfigRoutingMiddleware =
  ({ history }) =>
  ({ getState, dispatch }) =>
  (next) =>
  async (action) => {
    // The v3Order reducer re-qualifies incentives when an item is removed,
    // and the offer path counts voucher value toward the qualifying total
    // (see eligibleIncentivesFor{Order,Cart}). Vouchers live in the
    // cartVoucherItems slice, which a slice reducer cannot read, so inject
    // the subtotal here — this middleware is last in the chain, so the
    // enriched action is what reaches the store. Doing it before
    // `next(action)` covers every dispatch site, including the thunk that
    // fans `removeCartItems` out into individual removals (ENG-2965).
    //
    // Not in the `removeCartItem` action creator: that is a plain creator
    // with no `getState`, so it would have to become a thunk, and even then
    // it would only cover callers that go through it — an action dispatched
    // as a bare `{ type: V3_REMOVE_CART_ITEM }` would still reach the reducer
    // without a subtotal. Enriching here is the one placement that cannot be
    // bypassed.
    if (action.type === V3_REMOVE_CART_ITEM) {
      action = {
        ...action,
        payload: {
          ...action.payload,
          voucherTotalCents:
            getState().cartVoucherItems?.voucherSubtotalCents || 0,
        },
      };
    }

    // A Tier 1 re-add (V3_ADD_INCENTIVE_TO_ORDER) puts the bonus straight into
    // its order with no configurator, so there is nothing to route to — except
    // that a parent who got here through the configurator's marker is owed the
    // trip back to the cart. The reducer clears that marker in this very
    // dispatch, so it has to be read BEFORE next(action) (ENG-2043 R7).
    const optionalIncentiveToConfigureBeforeAdd =
      action.type === V3_ADD_INCENTIVE_TO_ORDER
        ? getState().v3Order?.optionalIncentiveToConfigure
        : null;

    // Call the next middleware in the chain
    next(action);

    const state = getState();
    const {
      additionalData: { selectedStudent },
      v3Order: { orders },
      shoot: {
        incentiveProducts,
        interstitialProducts,
        retouchingProducts,
        suggestedProducts,
        additionalOfferProducts,
        shoot: shootInfo,
      },
      cartVoucherItems,
    } = state;

    const shootEventId = shootInfo?.event_id || shootInfo?.id;

    // Wait for promise to resolve before proceeding
    if (action.payload instanceof Promise) {
      try {
        const resolvedPayload = await action.payload;
        action = { ...action, payload: resolvedPayload };
      } catch (error) {
        // Handle error if promise rejects
        console.error('Error in action:', action.type, error);
      }
    } else {
      if (action.type === V3_ADD_INCENTIVE_TO_ORDER) {
        // No offers funnel for a direct bonus add: the parent never left the
        // cart (or, from the configurator's marker, is being returned to it).
        // Only a marker naming THIS bonus earns the trip back — a marker left
        // pending by some other bonus must not navigate on an unrelated add.
        if (
          cartUtils.optionalIncentiveMarkerMatchesProduct(
            optionalIncentiveToConfigureBeforeAdd,
            {
              productId: action.payload?.productId,
              sku: action.payload?.sku ?? null,
              incentiveAppliesTo:
                action.payload?.cartObject?.incentiveAppliesTo,
            },
          )
        ) {
          return dispatch(push('/v3/cart'));
        }

        return;
      }

      // Add to cart
      // Redirect in the following order:
      // 1. If retouching is required, redirect to retouching UI
      // 2. If interstitial product should be prompted, redirect to interstitial UI
      // 3. If incetnive product has been activated, redirect to incentive UI
      // 4. If 'suggested products' category is present, redirect to suggested products UI
      if (
        action.type === V3_ADD_TO_CART ||
        action.type === ADD_VOUCHER_TO_CART ||
        action.type === V3_SUBMIT_PERSONALIZATION ||
        action.type === V3_DECLINE_PERSONALIZATION ||
        action.type === V3_SUBMIT_PERSONALIZATION ||
        action.type === V3_DECLINE_INTERSTITIAL_PRODUCT
      ) {
        // Re-add flow: a bonus added back via the cart's "Available Bonuses"
        // section (which sets optionalIncentiveToConfigure before routing to
        // the incentive config UI) should land back on the cart once
        // configured instead of restarting the offers funnel.
        //
        // Driven by the ACTION PAYLOAD, not by the marker in state (ENG-2043
        // R7): this middleware reads getState() after next(action), and the
        // reducer clears the marker as part of the very add being routed —
        // reading it here would find it already gone. addToCart stamps
        // `optionalIncentiveReAdd` when the marker matched, naming both the
        // product and the order it belongs to; both are required, since an
        // add with no target order is an ordinary add.
        const optionalIncentiveReAdd = action.payload?.optionalIncentiveReAdd;
        if (
          action.type === V3_ADD_TO_CART &&
          optionalIncentiveReAdd?.orderKey &&
          optionalIncentiveReAdd?.productId &&
          cartUtils.optionalIncentiveMarkerMatchesProduct(
            optionalIncentiveReAdd,
            {
              productId: action.payload?.cartObject?.productId,
              sku: action.payload?.cartObject?.sku ?? null,
              incentiveAppliesTo:
                action.payload?.cartObject?.incentiveAppliesTo,
            },
          )
        ) {
          return dispatch(push('/v3/cart'));
        }

        // Get voucher total from state for incentive calculations
        const voucherTotalCents = cartVoucherItems?.voucherSubtotalCents || 0;
        const hasVouchers = cartVoucherItems?.items?.length > 0;

        console.log('[VOUCHER_MIDDLEWARE] Action:', action.type, {
          voucherTotalCents,
          hasVouchers,
          incentiveProductsCount: incentiveProducts?.length || 0,
        });

        // Handle case where selectedStudent might not exist (voucher-only scenario)
        const currentOrderKey =
          selectedStudent?.id && shootEventId
            ? `${shootEventId}-${selectedStudent.id}`
            : null;
        const currentOrder = currentOrderKey ? orders[currentOrderKey] : null;
        const cartItems = currentOrder?.cartItems || [];
        const shouldPromptRetouching = currentOrder?.retouching?.shouldPrompt;
        const shouldPromptPersonalization =
          currentOrder?.personalization?.shouldPrompt;
        // Whether a decline suppresses a re-prompt is decided per order now
        // (ENG-2043 R1/R3): the selectors gate the order-level list on the
        // CURRENT order's own opt-in flag and the cart-level list on the
        // shared-SKU rule, so the active shoot's flag is no longer consulted
        // here at all.
        const ignoredOrderIncentiveIds = selectIgnoredOrderIncentiveIds(state);
        const ignoredCartLevelIncentiveSkus =
          selectIgnoredCartLevelIncentiveSkus(state);

        console.log(
          'middleware -- should prompt personalization: ',
          shouldPromptPersonalization,
        );

        const cartItemsIncludeRetouching =
          cartUtils.cartItemsIncludeRetouching(cartItems);

        const cartItemsContainRetouchingProduct =
          cartUtils.cartItemsContainRetouchingProduct(cartItems);

        const incentiveProductsForRestriction =
          selectRestrictionAwareIncentiveProducts(state);

        const eligibleIncentivesForCart = cartUtils.eligibleIncentivesForCart(
          orders,
          // Cart-level incentives can live on any price list in the cart,
          // not just the active shoot's (ENG-2965).
          selectCartLevelIncentiveProducts(state),
          voucherTotalCents,
          ignoredCartLevelIncentiveSkus,
        );

        const eligibleIncentivesForOrder = cartUtils.eligibleIncentivesForOrder(
          cartItems,
          incentiveProductsForRestriction,
          voucherTotalCents,
          hasVouchers,
          ignoredOrderIncentiveIds,
        );

        const interstitialProductsToPrompt =
          cartUtils.interstitialProductsToPrompt(
            interstitialProducts,
            currentOrder?.interstitialProductsPrompted || [],
            cartItems,
          );

        // Vouchers count as non-incentive products for eligibility
        const cartContainsNonIncentiveProducts =
          cartUtils.cartContainsNonIncentiveProducts(cartItems) || hasVouchers;

        console.log('[VOUCHER_MIDDLEWARE] Incentive check:', {
          cartContainsNonIncentiveProducts,
          eligibleIncentivesForCart: eligibleIncentivesForCart?.length || 0,
          eligibleIncentivesForOrder: eligibleIncentivesForOrder?.length || 0,
          restricted: selectShippingRestricted(state),
          availableIncentiveCount: incentiveProductsForRestriction?.length || 0,
          incentiveProducts: incentiveProducts?.map((p) => ({
            id: p.id,
            name: p.name,
            threshold: p.incentive_threshold,
            applies_to: p.incentive_applies_to,
          })),
        });

        if (
          cartContainsNonIncentiveProducts &&
          eligibleIncentivesForCart &&
          eligibleIncentivesForCart?.length > 0
        ) {
          console.log('ROUTING TO *INCENTIVES FOR CART* VIA MIDDLEWARE');
          return dispatch(push('/v3/incentives/config'));
        }

        if (
          cartContainsNonIncentiveProducts &&
          eligibleIncentivesForOrder &&
          eligibleIncentivesForOrder?.length > 0
        ) {
          console.log('ROUTING TO INCENTIVES VIA MIDDLEWARE');
          return dispatch(push('/v3/incentives/config'));
        }

        // Exclude pure group products (every pose slot a group photo) from the
        // interstitial offers.
        const excludeGroupImageProducts = (products) => {
          return products.filter((product) => !productIsAllGroup(product));
        };

        // Check if there's a group product in the cart
        const hasGroupCartItem = cartItems.some((item) => item.hasGroupRecord);

        // Decide if we need to exclude group_image products
        const filteredInterstitialProducts = hasGroupCartItem
          ? excludeGroupImageProducts(interstitialProductsToPrompt)
          : interstitialProductsToPrompt;

        // If there are any remaining interstitial products, route to your desired path
        if (filteredInterstitialProducts.length > 0) {
          console.log('ROUTING TO INTERSTITIAL OFFER VIA MIDDLEWARE');
          return dispatch(push('/v3/interstitial'));
        }

        // Redirect to retouching UI as needed
        if (
          shouldPromptRetouching &&
          !cartItemsIncludeRetouching &&
          !cartItemsContainRetouchingProduct &&
          retouchingProducts?.length > 0 &&
          !cartUtils.cartItemsAreGroupProductsOnly(cartItems)
        ) {
          console.log('ROUTING TO RETOUCHING VIA MIDDLEWARE');
          return dispatch(push('/v3/retouching'));
        }

        if (
          action.type === V3_ADD_TO_CART &&
          action.payload?.cartObject?.isPreCartOfferProduct
        ) {
          if (action.payload.redirectSource === 'dashboard') {
            console.log(
              'ADDED PRE-CART OFFER PRODUCT; ROUTING TO DASHBOARD VIA MIDDLEWARE',
            );
            return dispatch(push('/dashboard'));
          }

          console.log(
            'ADDED PRE-CART OFFER PRODUCT; ROUTING TO CART VIA MIDDLEWARE',
          );
          return dispatch(push('/v3/cart'));
        }

        // redirect to 'suggested products' UI as needed
        if (suggestedProducts && suggestedProducts?.length > 0) {
          console.log('ROUTING TO SUGGESTED PRODUCTS VIA MIDDLEWARE');
          return dispatch(push('/v3/suggested-products'));
        }

        if (additionalOfferProducts && additionalOfferProducts?.length > 0) {
          return dispatch(push('/v3/additional-offers'));
        }

        // ...otherwise, redirect to primary offers UI
        console.log('ROUTING TO OFFERS VIA MIDDLEWARE');
        dispatch(push('/v3/offers'));
      }

      if (action.type === V3_DECLINE_PRE_CART_OFFER_PRODUCT) {
        if (action.payload.redirectSource === 'dashboard') {
          console.log(
            'DECLINED PRE-CART OFFER PRODUCT; ROUTING TO DASHBOARD VIA MIDDLEWARE',
          );
          return dispatch(push('/dashboard'));
        }
        console.log('ROUTING TO CART VIA MIDDLEWARE');
        dispatch(push('/v3/cart'));
      }

      // The following assumes that the user adds/skips personalization on an
      // interstitial UI while en route to the cart
      if (
        action.type === V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS ||
        action.type === V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS
      ) {
        const personalizationOrderKey = `${shootEventId}-${selectedStudent.id}`;
        const currentOrder = orders[personalizationOrderKey];
        if (!currentOrder.personalization?.shouldPrompt) {
          if (action.payload.redirectSource === 'dashboard') {
            return dispatch(push('/dashboard'));
          }

          return dispatch(push('/v3/cart'));
        }
      }

      // Decline retouching
      if (action.type === V3_DECLINE_RETOUCHING) {
        if (suggestedProducts && suggestedProducts?.length > 0) {
          console.log('ROUTING TO SUGGESTED PRODUCTS VIA MIDDLEWARE');
          return dispatch(push('/v3/suggested-products'));
        }

        dispatch(push('/v3/offers'));
      }

      if (action.type === V3_DECLINE_PERSONALIZATION) {
        dispatch(push('/v3/offers'));
      }

      // Reset retouching prompt status if user removes all retouching items
      // from cart.
      //
      // This is the second V3_REMOVE_CART_ITEM block in this function, and it
      // cannot be merged with the one at the top: that one enriches the action
      // *before* `next(action)` so the reducer sees the voucher subtotal, while
      // this one reads `orders` from the state *after* the reducer has already
      // removed the item. They are two phases, not two copies.
      if (action.type === V3_REMOVE_CART_ITEM) {
        const { orderKey, cartItemId } = action.payload;
        const cartItems = orders[orderKey]?.cartItems;

        console.log('[NTH_FREE_DEBUG] Remove cart item triggered:', {
          cartItemId,
          orderKey,
          cartItemsCount: cartItems?.length || 0,
        });

        // After any cart item removal, check if nth free products should be removed
        // This runs AFTER the item has been removed, so we check the remaining cart
        if (cartItems && cartItems.length > 0) {
          // Find ALL nth free qualifiers (products that can trigger free items)
          const nthFreeQualifiers = cartItems.filter(
            (item) => item.isNthFreeQualifier === true,
          );

          // Find ALL nth free products in the cart
          const nthFreeProducts = cartItems.filter(
            (item) => item.isNthFreeProduct === true,
          );

          console.log('[NTH_FREE_DEBUG] Cart analysis:', {
            qualifiersCount: nthFreeQualifiers.length,
            freeProductsCount: nthFreeProducts.length,
            qualifiers: nthFreeQualifiers.map((q) => ({
              id: q.id,
              productId: q.productId,
              name: q.productName,
              price: q.price,
              nthFreeThreshold: q.nthFreeThreshold,
              nthFreeValue: q.nthFreeValue,
            })),
            freeProducts: nthFreeProducts.map((p) => ({
              id: p.id,
              productId: p.productId,
              name: p.productName,
              price: p.price,
              nthFreeValue: p.nthFreeValue,
            })),
          });

          // If there are free products but no qualifiers, remove all free products
          if (nthFreeProducts.length > 0 && nthFreeQualifiers.length === 0) {
            console.log(
              '[NTH_FREE_DEBUG] No qualifiers remain, removing all free products',
            );
            nthFreeProducts.forEach((product) => {
              dispatch(removeCartItem(product.id, orderKey));
            });
            return; // Exit early since we removed everything
          }

          // If we have both qualifiers and free products, do the math
          if (nthFreeQualifiers.length > 0 && nthFreeProducts.length > 0) {
            // Get the threshold (assuming all qualifiers have the same threshold)
            const threshold = nthFreeQualifiers[0]?.nthFreeThreshold || 2;

            // Calculate how many free products are allowed
            const allowedFreeProducts = Math.floor(
              nthFreeQualifiers.length / threshold,
            );

            // Get the highest value among qualifiers (free products must be equal or lesser value)
            const highestQualifierValue = Math.max(
              ...nthFreeQualifiers.map((q) => q.price || 0),
            );

            console.log('[NTH_FREE_DEBUG] Nth free calculation:', {
              qualifierCount: nthFreeQualifiers.length,
              threshold: threshold,
              allowedFreeProducts: allowedFreeProducts,
              actualFreeProducts: nthFreeProducts.length,
              highestQualifierValue: highestQualifierValue,
            });

            // Sort free products by value (descending) to remove most expensive first if needed
            const sortedFreeProducts = [...nthFreeProducts].sort(
              (a, b) =>
                (b.nthFreeValue || b.price || 0) -
                (a.nthFreeValue || a.price || 0),
            );

            // Remove free products that exceed the allowed amount
            if (nthFreeProducts.length > allowedFreeProducts) {
              const productsToRemove =
                nthFreeProducts.length - allowedFreeProducts;
              console.log(
                '[NTH_FREE_DEBUG] Need to remove excess free products:',
                {
                  currentCount: nthFreeProducts.length,
                  allowedCount: allowedFreeProducts,
                  toRemoveCount: productsToRemove,
                },
              );

              // Remove the excess free products (starting with most expensive)
              for (let i = 0; i < productsToRemove; i++) {
                const productToRemove = sortedFreeProducts[i];
                console.log('[NTH_FREE_DEBUG] Removing excess free product:', {
                  id: productToRemove.id,
                  name: productToRemove.productName,
                  value: productToRemove.nthFreeValue || productToRemove.price,
                });
                dispatch(removeCartItem(productToRemove.id, orderKey));
              }
            }

            // Also check value constraint: free products should not exceed highest qualifier value
            // This is typically enforced when adding to cart, but we can double-check
            const invalidFreeProducts = nthFreeProducts.filter((fp) => {
              const freeValue = fp.nthFreeValue || fp.price || 0;
              return freeValue > highestQualifierValue;
            });

            if (invalidFreeProducts.length > 0) {
              console.log(
                '[NTH_FREE_DEBUG] Found free products exceeding qualifier value:',
                {
                  highestQualifierValue: highestQualifierValue,
                  invalidProducts: invalidFreeProducts.map((p) => ({
                    id: p.id,
                    name: p.productName,
                    value: p.nthFreeValue || p.price,
                  })),
                },
              );

              invalidFreeProducts.forEach((product) => {
                console.log(
                  '[NTH_FREE_DEBUG] Removing invalid free product (exceeds value):',
                  product.id,
                );
                dispatch(removeCartItem(product.id, orderKey));
              });
            }
          }

          // Also check for orphaned free products (price = 0, productType = 'nth_free' but missing flag)
          const orphanedFreeProducts = cartItems.filter(
            (item) =>
              item.price === 0 &&
              item.productType === 'nth_free' &&
              !item.isNthFreeProduct,
          );

          if (orphanedFreeProducts.length > 0) {
            console.log('[NTH_FREE_DEBUG] Found orphaned free products:', {
              count: orphanedFreeProducts.length,
              products: orphanedFreeProducts.map((p) => ({
                id: p.id,
                productId: p.productId,
                name: p.productName,
              })),
            });

            // If no qualifiers, remove orphaned products too
            if (nthFreeQualifiers.length === 0) {
              orphanedFreeProducts.forEach((product) => {
                dispatch(removeCartItem(product.id, orderKey));
              });
            }
          }
        }

        const unlockedGroups = cartUtils.unlockedGroups(cartItems);

        if (unlockedGroups?.length > 0) {
          const productIdsWithLockedBackgrounds =
            cartUtils.productIdsWithLockedBackgrounds(
              cartItems,
              unlockedGroups,
            );
          dispatch(removeCartItems(productIdsWithLockedBackgrounds, orderKey));
        }

        const lockedProductIds = cartUtils.lockedProductIds(cartItems);

        if (cartUtils.hasLockedProductsNoUnlockers(cartItems)) {
          if (lockedProductIds.length > 0) {
            dispatch(removeCartItems(lockedProductIds, orderKey));
          }
        }

        if (
          cartUtils.orderHasNoRetouching(orders && cartItems) &&
          orders[orderKey].retouching.shouldPrompt === false
        ) {
          dispatch(resetRetouching(orderKey));
        }

        if (cartItems?.length === 1 && cartItems[0].isPreCartOfferProduct) {
          dispatch(removeCartItem(cartItems[0].id, orderKey));
        }

        if (
          cartItems &&
          cartItems.length > 0 &&
          cartItems.every(
            (item) =>
              item.isPreCartOfferProduct ||
              item.isRetouching ||
              item.isPersonalization,
          )
        ) {
          dispatch(
            removeCartItems(
              cartUtils.preCartOfferProductIds(cartItems),
              orderKey,
            ),
          );
        }

        // Check if cart is now empty and clear voucher items if so
        // Need to get fresh state after all the above removals may have happened
        const updatedState = getState();
        const updatedOrders = updatedState.v3Order.orders;
        const cartUuid = updatedState.v3Order.uuid;
        const hasVoucherItems =
          updatedState.cartVoucherItems?.items?.length > 0;

        // Check if all orders are now empty (no student orders, or all have empty carts)
        const allOrdersEmpty =
          Object.keys(updatedOrders).length === 0 ||
          Object.values(updatedOrders).every(
            (order) => !order.cartItems || order.cartItems.length === 0,
          );

        // Also check if only incentive/free items remain (which will be removed by the reducer)
        const onlyIncentivesRemain = Object.values(updatedOrders).every(
          (order) => {
            const items = order.cartItems || [];
            return (
              items.length === 0 || items.every((item) => item.isIncentive)
            );
          },
        );

        if ((allOrdersEmpty || onlyIncentivesRemain) && hasVoucherItems) {
          console.log(
            '[VOUCHER_MIDDLEWARE] Cart is empty, clearing voucher items',
          );
          dispatch(clearCartVoucherItems(cartUuid));
        }
      }

      // Handle voucher removal - check if incentive products should also be removed
      if (action.type === REMOVE_CART_VOUCHER_ITEM) {
        // Get fresh state after voucher removal
        const updatedState = getState();
        const updatedOrders = updatedState.v3Order.orders;
        const updatedVoucherItems = updatedState.cartVoucherItems;
        const newVoucherTotalCents =
          updatedVoucherItems?.voucherSubtotalCents || 0;
        const hasVouchers = updatedVoucherItems?.items?.length > 0;

        console.log(
          '[VOUCHER_MIDDLEWARE] Voucher removed, checking incentive eligibility:',
          {
            newVoucherTotalCents,
            hasVouchers,
          },
        );

        // Check each order for incentive products that are no longer eligible.
        // Note this path intentionally checks cart-level eligibility against
        // the active catalog only (no cross-price-list union): it is deciding
        // which incentives to *strip* after a voucher was removed, and
        // ENG-2965 did not change that.
        const incentiveProductsForRestriction =
          selectRestrictionAwareIncentiveProducts(updatedState);

        Object.entries(updatedOrders).forEach(([orderKey, order]) => {
          const cartItems = order?.cartItems || [];
          const incentiveItems = cartItems.filter((item) => item.isIncentive);

          if (incentiveItems.length === 0) return;

          // Calculate what incentives should still be eligible with the reduced voucher total
          const stillEligibleOrderIncentives =
            cartUtils.eligibleIncentivesForOrder(
              cartItems.filter((item) => !item.isIncentive),
              incentiveProductsForRestriction,
              newVoucherTotalCents,
              hasVouchers,
            );

          const stillEligibleCartIncentives =
            cartUtils.eligibleIncentivesForCart(
              updatedOrders,
              incentiveProductsForRestriction,
              newVoucherTotalCents,
            );

          const stillEligibleIds = [
            ...stillEligibleOrderIncentives.map((p) => p.id),
            ...stillEligibleCartIncentives.map((p) => p.id),
          ];

          console.log('[VOUCHER_MIDDLEWARE] Incentive eligibility check:', {
            orderKey,
            incentiveItemsCount: incentiveItems.length,
            stillEligibleIds,
          });

          // Remove incentive items that are no longer eligible
          incentiveItems.forEach((item) => {
            if (!stillEligibleIds.includes(item.productId)) {
              console.log(
                '[VOUCHER_MIDDLEWARE] Removing ineligible incentive:',
                {
                  itemId: item.id,
                  productId: item.productId,
                  orderKey,
                },
              );
              // App-initiated cleanup, not a parent decline: the bonus lost
              // its qualifying spend when the voucher went away, and the
              // parent never said "no thanks" to it (ENG-2043 R6).
              dispatch(
                removeCartItem(item.id, orderKey, { recordDecline: false }),
              );
            }
          });
        });
      }
    }

    // Proceed with next middleware or reducer
    return action;
  };

export default postConfigRoutingMiddleware;
